import parser from 'any-date-parser';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Checkbox,
  Container,
  Divider,
  Grid,
  NumberInput,
  PasswordInput,
  SegmentedControl,
  Select,
  Text,
  TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { handleSubmitError } from '@utils/forms';

import { CustomerType, Gender } from '@interfaces/customer.interface';

import { useCreateCustomerMutation } from '@api/customers.api';

import useQueryParams from '@hooks/queryParams/useQueryParams';
import useFormEnhanced from '@hooks/useFormEnhanced';
import useGRecaptcha from '@hooks/useGRecaptcha';

import {
  CitySelector,
  NationSelector,
  ProvinceSelector,
  ZipSelector,
} from '@components/common/locations';

import classes from './RegisterForm.module.css';
import PasswordInputWithRequirements from '@components/PasswordInputWithRequirements';

export default function RegisterForm() {
  // ==========================================================================
  // General
  // ==========================================================================
  const [params, setParams] = useQueryParams();
  const { t } = useTranslation();

  const { getRecaptchaToken } = useGRecaptcha();

  // ==========================================================================
  // State
  // ==========================================================================
  const [isDomicileDifferent, setIsDomicileDifferent] = useState(false);

  // ==========================================================================
  // Form
  // ==========================================================================
  const initialValues = {
    email: '', // required
    emailConfirm: '',
    password: '',
    passwordConfirm: '', // required
    type: 'private' as CustomerType,
    firstName: '',
    lastName: '',
    companyName: '',
    vatTaxNumber: '',
    pec: '',
    sdi: '',
    gender: '',
    address: '',
    city: '',
    zip: '',
    country: '',
    province: '',
    birthDate: undefined,
    birthCity: '',
    height: undefined,
    nationality: '',
    phone: '',
    domicileCountry: '',
    domicileProvince: '',
    domicileCity: '',
    domicileZip: '',
    domicileAddress: '',
    privacy: false,
    newsLetter: false, // if true add user in MagNews service
  };

  const form = useFormEnhanced({
    mode: 'uncontrolled',
    initialValues,
    validate: {
      emailConfirm: (value, values) =>
        value !== values.email
          ? t('auth.register.validation.emailMismatch')
          : null,
      passwordConfirm: (value, values) =>
        value !== values.password
          ? t('auth.register.validation.passwordMismatch')
          : null,
    },
    normalizeFields: {
      capitalizeFields: [
        'firstName',
        'lastName',
        'companyName',
        'birthCity',
        'address',
        'nationality',
        'domicileAddress',
      ],
      lowercaseFields: ['email', 'pec'],
      uppercaseFields: ['vatTaxNumber', 'sdi'],
    },
  });

  // ==========================================================================
  // Api
  // ==========================================================================
  const [createCustomer, { isLoading }] = useCreateCustomerMutation();

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const onSubmit = async (values: typeof initialValues) => {
    try {
      const recaptchaToken = await getRecaptchaToken('SignUp');

      const mappedValues = {
        email: values.email,
        password: values.password,
        firstName: values.firstName,
        lastName: values.lastName,
        type: values.type,
        companyName:
          values.companyName.trim() !== '' ? values.companyName : undefined,
        vatTaxNumber:
          values.vatTaxNumber.trim() !== '' ? values.vatTaxNumber : undefined,
        pec: values.pec.trim() !== '' ? values.pec : undefined,
        sdi: values.sdi.trim() !== '' ? values.sdi : undefined,
        gender: values.gender as Gender | undefined,
        birthDate: values.birthDate
          ? new Date(values.birthDate).toISOString()
          : undefined,
        birthCity: values.birthCity,
        height: values.height,
        phone: values.phone,
        country: values.country.trim() !== '' ? values.country : undefined,
        province: values.province.trim() !== '' ? values.province : undefined,
        city: values.city.trim() !== '' ? values.city : undefined,
        zip: values.zip.trim() !== '' ? values.zip : undefined,
        address: values.address.trim() !== '' ? values.address : undefined,
        nationality:
          values.nationality.trim() !== '' ? values.nationality : undefined,
        domicileCountry:
          values.type === 'private'
            ? values.domicileCountry.trim() !== ''
              ? values.domicileCountry
              : values.country
            : undefined,
        domicileProvince:
          values.type === 'private'
            ? values.domicileProvince.trim() !== ''
              ? values.domicileProvince
              : values.province
            : undefined,
        domicileCity:
          values.type === 'private'
            ? values.domicileCity.trim() !== ''
              ? values.domicileCity
              : values.province
            : undefined,
        domicileZip:
          values.type === 'private'
            ? values.domicileZip.trim() !== ''
              ? values.domicileZip
              : values.zip
            : undefined,
        domicileAddress:
          values.type === 'private'
            ? values.domicileAddress.trim() !== ''
              ? values.domicileAddress
              : values.address
            : undefined,
      };

      await createCustomer({ body: mappedValues, recaptchaToken }).unwrap();

      params.set('p', 'login');
      params.set('alert', t('auth.register.successMessage'));
      setParams(params);
    } catch (e) {
      handleSubmitError(e, form);
    }
  };

  // ==========================================================================
  // Render
  // ==========================================================================
  return (
    <Container w="100%" size="40rem">
      <Text ta="center" fw="bold" size="1.5rem">
        {t('auth.register.title')}
      </Text>

      <Text ta="center" mt="md">
        {t('auth.register.hasAccount')}{' '}
        <Text
          span
          c="brand"
          style={{ cursor: 'pointer' }}
          onClick={() => {
            params.set('p', 'login');
            setParams(params);
          }}
        >
          {t('auth.register.loginLink')}
        </Text>
      </Text>

      <form
        onSubmit={form.onSubmit((values) => {
          onSubmit(values);
        })}
      >
        <Divider
          label={t('auth.register.accountInfo')}
          labelPosition="left"
          mt="xl"
          mb="sm"
          classNames={{ label: classes.dividerLabel }}
        />

        <Grid>
          <Grid.Col span={{ md: 6 }}>
            <TextInput
              label={t('auth.register.email.label')}
              placeholder={t('auth.register.email.placeholder')}
              key={form.key('email')}
              required
              {...form.getInputProps('email')}
            />
          </Grid.Col>
          <Grid.Col span={{ md: 6 }}>
            <TextInput
              label={t('auth.register.emailConfirm.label')}
              placeholder={t('auth.register.emailConfirm.placeholder')}
              key={form.key('emailConfirm')}
              required
              {...form.getInputProps('emailConfirm')}
            />
          </Grid.Col>

          <Grid.Col span={{ md: 6 }}>
            <PasswordInputWithRequirements
              label={t('auth.register.password.label')}
              placeholder={t('auth.register.password.placeholder')}
              key={form.key('password')}
              required
              {...form.getInputProps('password')}
            />
          </Grid.Col>
          <Grid.Col span={{ md: 6 }}>
            <PasswordInput
              label={t('auth.register.passwordConfirm.label')}
              placeholder={t('auth.register.passwordConfirm.placeholder')}
              key={form.key('passwordConfirm')}
              required
              {...form.getInputProps('passwordConfirm')}
            />
          </Grid.Col>
        </Grid>

        <Divider
          label={t('auth.register.baseInfo')}
          labelPosition="left"
          mt="xl"
          mb="sm"
          classNames={{ label: classes.dividerLabel }}
        />

        <SegmentedControl
          mb="md"
          mx="auto"
          data={[
            { label: t('auth.register.private'), value: 'private' },
            { label: t('auth.register.company'), value: 'company' },
          ]}
          {...form.getInputProps('type')}
        />

        <Grid>
          <Grid.Col
            span={{ md: 4 }}
            style={{ display: 'flex', alignItems: 'end' }}
          >
            <TextInput
              label={t('auth.register.firstName.label')}
              placeholder={t('auth.register.firstName.placeholder')}
              key={form.key('firstName')}
              required
              {...form.getInputProps('firstName')}
            />
          </Grid.Col>
          <Grid.Col
            span={{ md: 4 }}
            style={{ display: 'flex', alignItems: 'end' }}
          >
            <TextInput
              label={t('auth.register.lastName.label')}
              placeholder={t('auth.register.lastName.placeholder')}
              key={form.key('lastName')}
              required
              {...form.getInputProps('lastName')}
            />
          </Grid.Col>
          <Grid.Col span={{ md: 4 }}>
            <TextInput
              label={
                form.values.type === 'company'
                  ? t('auth.register.vatTaxNumber.labelCompany')
                  : t('auth.register.vatTaxNumber.labelPrivate')
              }
              placeholder={
                form.values.type === 'company'
                  ? t('auth.register.vatTaxNumber.placeholderCompany')
                  : t('auth.register.vatTaxNumber.placeholderPrivate')
              }
              key={form.key('vatTaxNumber')}
              required={form.values.country === 'Italia'}
              description={t('auth.register.vatTaxNumber.description')}
              {...form.getInputProps('vatTaxNumber')}
            />
          </Grid.Col>
          <Grid.Col span={{ md: 4 }}>
            {form.values.type === 'company' ? (
              <TextInput
                label={t('auth.register.companyName.label')}
                placeholder={t('auth.register.companyName.placeholder')}
                key={form.key('companyName')}
                required
                {...form.getInputProps('companyName')}
              />
            ) : (
              <Select
                label={t('auth.register.gender.label')}
                placeholder={t('auth.register.gender.placeholder')}
                key={form.key('gender')}
                required
                data={[
                  { label: t('auth.register.gender.male'), value: 'male' },
                  { label: t('auth.register.gender.female'), value: 'female' },
                ]}
                {...form.getInputProps('gender')}
              />
            )}
          </Grid.Col>
          {form.values.type === 'company' && (
            <>
              <Grid.Col span={{ md: 4 }}>
                <TextInput
                  label={t('auth.register.pec.label')}
                  placeholder={t('auth.register.pec.placeholder')}
                  key={form.key('pec')}
                  required
                  {...form.getInputProps('pec')}
                />
              </Grid.Col>
              <Grid.Col span={{ md: 4 }}>
                <TextInput
                  label={t('auth.register.sdi.label')}
                  placeholder={t('auth.register.sdi.placeholder')}
                  key={form.key('sdi')}
                  required
                  {...form.getInputProps('sdi')}
                />
              </Grid.Col>
            </>
          )}

          <Grid.Col span={{ md: 4 }}>
            <TextInput
              label={t('auth.register.phone.label')}
              placeholder={t('auth.register.phone.placeholder')}
              key={form.key('phone')}
              required
              {...form.getInputProps('phone')}
            />
          </Grid.Col>
          <Grid.Col span={{ md: 4 }}>
            {form.values.type === 'private' && (
              <NumberInput
                label={t('auth.register.height.label')}
                placeholder={t('auth.register.height.placeholder')}
                key={form.key('height')}
                required
                min={50}
                {...form.getInputProps('height')}
              />
            )}
          </Grid.Col>

          {form.values.type === 'private' && (
            <>
              <Grid.Col span={{ md: 4 }}>
                <DateInput
                  label={t('auth.register.birthDate.label')}
                  placeholder={t('auth.register.birthDate.placeholder')}
                  key={form.key('birthDate')}
                  required
                  valueFormat="DD/MM/YYYY"
                  dateParser={(value) =>
                    parser.fromString(value, 'it').toISOString()
                  }
                  {...form.getInputProps('birthDate')}
                />
              </Grid.Col>
              <Grid.Col span={{ md: 4 }}>
                <TextInput
                  label={t('auth.register.birthCity.label')}
                  placeholder={t('auth.register.birthCity.placeholder')}
                  key={form.key('birthCity')}
                  required
                  {...form.getInputProps('birthCity')}
                />
              </Grid.Col>
            </>
          )}
          {form.values.type === 'private' && (
            <Grid.Col span={{ md: 4 }}>
              <TextInput
                label={t('auth.register.nationality.label')}
                placeholder={t('auth.register.nationality.placeholder')}
                key={form.key('nationality')}
                required
                {...form.getInputProps('nationality')}
              />
            </Grid.Col>
          )}
        </Grid>

        <Divider
          label={t('auth.register.residence')}
          labelPosition="left"
          mt="xl"
          mb="sm"
          classNames={{ label: classes.dividerLabel }}
        />

        <Grid>
          <Grid.Col span={{ md: 4 }}>
            <NationSelector
              placeholder={t('auth.register.country.placeholder')}
              key={form.key('country')}
              required
              {...form.getInputProps('country')}
            />
          </Grid.Col>
          <Grid.Col span={{ md: 4 }}>
            {form.values.country === 'Italia' ? (
              <ProvinceSelector
                key={form.key('province')}
                required
                placeholder={t('auth.register.province.placeholder')}
                {...form.getInputProps('province')}
              />
            ) : (
              <TextInput
                label={t('auth.register.city.label')}
                placeholder={t('auth.register.city.placeholderResidence')}
                autoComplete="off"
                key={form.key('city')}
                required
                {...form.getInputProps('city')}
              />
            )}
          </Grid.Col>

          {form.values.country === 'Italia' && (
            <>
              <Grid.Col span={{ md: 4 }}>
                <CitySelector
                  key={form.key('city')}
                  required
                  placeholder={t('auth.register.city.placeholderResidence')}
                  province={form.values.province}
                  {...form.getInputProps('city')}
                  disabled={form.values.province === ''}
                />
              </Grid.Col>
              <Grid.Col span={{ md: 4 }}>
                <ZipSelector
                  key={form.key('zip')}
                  required
                  placeholder={t('auth.register.zip.placeholder')}
                  city={form.values.city}
                  {...form.getInputProps('zip')}
                  disabled={form.values.city === ''}
                />
              </Grid.Col>
            </>
          )}
          <Grid.Col span={{ md: 4 }}>
            <TextInput
              label={t('auth.register.address.label')}
              placeholder={t('auth.register.address.placeholderResidence')}
              autoComplete="off"
              key={form.key('address')}
              required
              {...form.getInputProps('address')}
            />
          </Grid.Col>
        </Grid>

        {form.values.type === 'private' && (
          <>
            <Divider
              label={t('auth.register.domicile')}
              labelPosition="left"
              mt="xl"
              mb="sm"
              classNames={{ label: classes.dividerLabel }}
            />

            <Checkbox
              w="max-content"
              mb="lg"
              mx="auto"
              label={t('auth.register.domicileDifferent')}
              checked={isDomicileDifferent}
              onChange={(e) => setIsDomicileDifferent(e.currentTarget.checked)}
            />

            {isDomicileDifferent && (
              <Grid>
                <Grid.Col span={{ md: 4 }}>
                  <NationSelector
                    placeholder={t('auth.register.domicileCountry.placeholder')}
                    key={form.key('domicileCountry')}
                    required
                    {...form.getInputProps('domicileCountry')}
                  />
                </Grid.Col>
                <Grid.Col span={{ md: 4 }}>
                  {form.values.domicileCountry === 'Italia' ? (
                    <ProvinceSelector
                      key={form.key('domicileProvince')}
                      required
                      placeholder={t(
                        'auth.register.domicileProvince.placeholder',
                      )}
                      {...form.getInputProps('domicileProvince')}
                    />
                  ) : (
                    <TextInput
                      label={t('auth.register.city.label')}
                      placeholder={t('auth.register.city.placeholderDomicile')}
                      autoComplete="off"
                      key={form.key('domicileCity')}
                      required
                      {...form.getInputProps('domicileCity')}
                    />
                  )}
                </Grid.Col>

                {form.values.domicileCountry === 'Italia' && (
                  <>
                    <Grid.Col span={{ md: 4 }}>
                      <CitySelector
                        key={form.key('domicileCity')}
                        required
                        placeholder={t(
                          'auth.register.domicileCity.placeholder',
                        )}
                        province={form.values.domicileProvince}
                        {...form.getInputProps('domicileCity')}
                        disabled={form.values.domicileProvince === ''}
                      />
                    </Grid.Col>
                    <Grid.Col span={{ md: 4 }}>
                      <ZipSelector
                        key={form.key('domicileZip')}
                        required
                        placeholder={t('auth.register.domicileZip.placeholder')}
                        city={form.values.domicileCity}
                        {...form.getInputProps('domicileZip')}
                        disabled={form.values.domicileCity === ''}
                      />
                    </Grid.Col>
                  </>
                )}
                <Grid.Col span={{ md: 4 }}>
                  <TextInput
                    label={t('auth.register.address.label')}
                    placeholder={t('auth.register.address.placeholderDomicile')}
                    autoComplete="off"
                    key={form.key('domicileAddress')}
                    required
                    {...form.getInputProps('domicileAddress')}
                  />
                </Grid.Col>
              </Grid>
            )}
          </>
        )}

        <Divider my="md" />

        <Checkbox
          w="max-content"
          mx="auto"
          label={t('auth.register.privacyConsent')}
          required
        />
        <Checkbox
          mt="sm"
          w="max-content"
          mx="auto"
          label={t('auth.register.dataConsent')}
          required
        />

        {form.errors.general && (
          <Text c="red" size="sm" ta="center" mx="auto" mt="xl">
            {form.errors.general}
          </Text>
        )}
        <Button
          type="submit"
          mt="xl"
          mx="auto"
          style={{ display: 'block' }}
          loading={isLoading}
        >
          {t('auth.register.submit')}
        </Button>
      </form>
    </Container>
  );
}
